import React from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { countListValues } from '@jwp/ott-common/src/utils/metadata';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useMovieData from '@jwp/ott-hooks-react/src/useMovieData';
import useMovieParams from '@jwp/ott-ui-react/src/hooks/useMovieParams';
import useMovieHandlers from '@jwp/ott-ui-react/src/hooks/useMovieHandlers';
import useTrailerData from '@jwp/ott-hooks-react/src/useTrailerData';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import VideoTags from '@jwp/ott-ui-react/src/components/VideoTags/VideoTags';
import VideoDefinitionList from '@jwp/ott-ui-react/src/components/VideoDefintionsList/VideoDefinitionList';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import MediaTabs from '@jwp/ott-ui-react/src/components/MediaTabs/MediaTabs';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import MediaTabAbout from '@jwp/ott-ui-react/src/components/MediaTabAbout/MediaTabAbout';
import MediaButtonTrailer from '@jwp/ott-ui-react/src/components/MediaButtonTrailer/MediaButtonTrailer';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import { generateMovieJSONLD } from '@jwp/ott-common/src/utils/structuredData';
import env from '@jwp/ott-common/src/env';

import MediaDescription from '../../../../components/MediaDescription/MediaDescription';

import styles from './MediaMovie.module.scss';

const MediaMovie: ScreenComponent<PlaylistItem> = ({ data, isLoading }) => {
  const { t } = useTranslation('video');
  const breakpoint = useBreakpoint();
  const { id, play, feedId } = useMovieParams();
  const {
    accessModel,
    isLoggedIn,
    hasSubscription,
    passes,
    playlist,
    playlistTitle,
    nextItem,
    playUrl,
    movieURL,
    isFavoritesEnabled,
    isSharingEnabled,
    isEntitled,
    primaryMetadata,
    isPlaylistLoading,
    videoRating,
    videoFormats,
    getCardUrl,
  } = useMovieData(data, id, feedId);
  const { handleBack, handleComplete } = useMovieHandlers(nextItem, movieURL, playlist);
  const { isTrailerPlaying, hasTrailer, trailerItem, trailerTitle, closeTrailer, playTrailer } = useTrailerData(data);

  const structuredData = generateMovieJSONLD(data, env.APP_PUBLIC_URL);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;
  const smallScreen = breakpoint < Breakpoint.md;

  useAutoFocus(id);

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} structuredData={structuredData} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          description={<MediaDescription item={data} />}
          metadataComponent={<VideoMetaData attributes={primaryMetadata} />}
          tagsComponent={<VideoTags attributes={videoFormats} />}
          ratingsComponent={<VideoTags attributes={videoRating} />}
          definitionComponent={
            <VideoDefinitionList
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.actors', { count: countListValues(data?.actors as string) }), value: data?.actors as string },
                { label: t('video:metadata.directors', { count: countListValues(data?.directors as string) }), value: data?.directors as string },
              ]}
            />
          }
        />
        <MediaHeroButtons
          cta={
            <>
              <StartWatchingButton
                key={id} // necessary to fix autofocus on TalkBack
                item={data}
                playUrl={playUrl}
              />
              {!smallScreen && (
                <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />
              )}
            </>
          }
        >
          {smallScreen && <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />}
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          {isSharingEnabled ? <ShareButton title={data.title} description={data.description} url={canonicalUrl} /> : undefined}
        </MediaHeroButtons>
      </MediaHero>
      <MediaTabs titles={[playlistTitle, t('video:about_tab')]} isLoading={isLoading || isPlaylistLoading}>
        {playlist && (
          <CardGrid
            playlist={playlist}
            isLoading={isLoading || isPlaylistLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            currentCardItem={data}
            currentCardLabel={t('video:current_video')}
            hasSubscription={hasSubscription}
            passes={passes}
            getUrl={getCardUrl}
          />
        )}
        <MediaTabAbout description={<MarkdownComponent className={styles.markdown} markdownString={data.description} headerOffset={1} />}>
          <div className={styles.aboutDefinitionList}>
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.directors', { count: countListValues(data?.directors as string) }), value: data?.directors as string },
                { label: t('video:metadata.cast'), value: data?.cast as string },
                { label: t('video:metadata.rating'), value: videoRating.length ? <VideoTags attributes={videoRating} /> : null },
                { label: t('video:metadata.formats'), value: videoFormats.length ? <VideoTags attributes={videoFormats} /> : null },
              ]}
            />
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.genres', { count: countListValues(data?.genre as string) }), value: data?.genre as string },
                { label: t('video:metadata.audio_languages'), value: data?.audioLanguages as string },
                { label: t('video:metadata.subtitle_languages'), value: data?.subtitleLanguages as string },
              ]}
            />
          </div>
        </MediaTabAbout>
      </MediaTabs>
      <Cinema
        open={play && isEntitled}
        onClose={handleBack}
        item={data}
        title={data.title}
        primaryMetadata={<VideoMetaData attributes={primaryMetadata} />}
        onComplete={handleComplete}
        feedId={feedId ?? undefined}
        onNext={handleComplete}
      />
      <TrailerModal item={trailerItem} title={trailerTitle} open={isTrailerPlaying} onClose={closeTrailer} />
    </React.Fragment>
  );
};

export default MediaMovie;
